/**
 * PublicConfigurator — the interactive client-facing experience.
 *
 * Replaces the static PublicProposal. The client browses options block by
 * block (single-select for flights/hotels/transfers, multi-select for
 * restaurants/activities/excursions) and sees a live trip summary update
 * in real time. Final CTA submits selections + client data to the admin.
 *
 * Design: see /app/design_guidelines.json — "Silent luxury", Aman/Soho House.
 * Backward compatibility: blocks with selection_mode='informational' or
 * single-item blocks render as inline cards without a "Seleccionar" button.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { publicApi, formatApiError, API } from "./api";
import {
  Lock, Plane, Hotel as HotelIcon, Car, Utensils, Sparkles, Compass, Train,
  ShieldCheck, Lightbulb, Check, ChevronLeft, ChevronRight, Send, Calendar, MapPin,
  ArrowUpRight, Printer, Phone, Mail, ArrowRight, ArrowDown, ArrowDownRight, Clock, FileText,
} from "lucide-react";
import { LOGO_URL } from "../data/escapafy";

// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────
const SINGLE_SELECT_BLOCKS = ["flights", "hotels", "transfers"];
const MULTI_SELECT_BLOCKS = ["restaurants", "activities", "excursions"];
const SELECTABLE_BLOCKS = [...SINGLE_SELECT_BLOCKS, ...MULTI_SELECT_BLOCKS];

const BLOCK_TITLES = {
  flights: "Vuelos",
  hotels: "Alojamiento",
  transfers: "Traslados",
  restaurants: "Restaurantes",
  activities: "Actividades",
  excursions: "Excursiones",
  transport: "Transporte",
  insurance: "Seguro de viaje",
  extras: "Información práctica",
};

const BLOCK_ICONS = {
  flights: Plane, hotels: HotelIcon, transfers: Car,
  restaurants: Utensils, activities: Sparkles, excursions: Compass,
  transport: Train, insurance: ShieldCheck, extras: Lightbulb,
};

const BLOCK_EYEBROWS = {
  flights: "Vuelos · Ida y vuelta",
  hotels: "Alojamiento curado",
  transfers: "Traslados",
  restaurants: "Mesa reservada",
  activities: "Para vivir",
  excursions: "Escapadas de un día",
};

const fmt = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(n || 0);

const toNum = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const absoluteUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

const itemImages = (item) => {
  const arr = Array.isArray(item.images) ? item.images.filter(Boolean) : [];
  if (arr.length) return arr.map(absoluteUrl);
  if (item.image_url) return [absoluteUrl(item.image_url)];
  return [];
};

const itemId = (item, idx) => item.id || `__idx_${idx}`;

const itemAmount = (item) => {
  const mode = item.pricing_mode || (toNum(item.amount) > 0 ? "included" : "");
  return mode === "included" ? toNum(item.amount) : 0;
};

const selectionModeOf = (block) => {
  const sm = block?.data?.selection_mode;
  if (sm) return sm;
  if (SINGLE_SELECT_BLOCKS.includes(block.type)) return "single";
  if (MULTI_SELECT_BLOCKS.includes(block.type)) return "multi";
  return "informational";
};

// ─────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────
export default function PublicConfigurator() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [pwd, setPwd] = useState("");
  const [showRequest, setShowRequest] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const autoPrintTriggered = useRef(false);

  // Selections state (persisted by token in localStorage)
  const storageKey = `escapafy_sel_${token}`;
  const [selections, setSelections] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch { /* noop */ }
    return { flights: null, hotels: null, transfers: null, insurance: null, restaurants: [], activities: [], excursions: [] };
  });

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(selections)); } catch { /* noop */ }
  }, [selections, storageKey]);

  const load = useCallback((password) => {
    setError(null);
    publicApi.getProposal(token, password).then(setData).catch((err) => setError(formatApiError(err)));
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/public/payment-settings`).then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // Auto-print when arriving from admin "Imprimir / PDF" link (?print=full|selection)
  useEffect(() => {
    if (!data || autoPrintTriggered.current) return;
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("print");
    if (mode === "full" || mode === "selection") {
      autoPrintTriggered.current = true;
      document.body.dataset.printMode = mode;
      const t = setTimeout(() => {
        window.print();
        const cleanup = () => { delete document.body.dataset.printMode; window.removeEventListener("afterprint", cleanup); };
        window.addEventListener("afterprint", cleanup);
      }, 800);
      return () => clearTimeout(t);
    }
  }, [data]);

  // Auto-select single options when block has selection_mode='single' and exactly 1 item
  useEffect(() => {
    if (!data) return;
    const next = { ...selections };
    let changed = false;
    (data.blocks || []).forEach((b) => {
      if (!SINGLE_SELECT_BLOCKS.includes(b.type)) return;
      if (selectionModeOf(b) !== "single") return;
      const items = b.data?.items || [];
      if (items.length === 1 && !selections[b.type]) {
        next[b.type] = itemId(items[0], 0);
        changed = true;
      }
    });
    if (changed) setSelections(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Compute live total
  const summary = useMemo(() => computeSummary(data, settings, selections), [data, settings, selections]);

  // ── Password gate
  if (error === "Contraseña requerida." || error === "Contraseña incorrecta.") {
    return <PasswordGate pwd={pwd} setPwd={setPwd} onSubmit={() => load(pwd)} error={error === "Contraseña incorrecta." ? error : null} />;
  }
  if (error) {
    return <FullCard>
      <p className="text-red-700 text-sm">{error}</p>
    </FullCard>;
  }
  if (!data) {
    return <FullCard><p className="text-[#3A506B] italic">Preparando tu propuesta…</p></FullCard>;
  }

  const blocks = (data.blocks || []).filter((b) => !b.hidden && b.type !== "private_notes");
  const tripBlock = blocks.find((b) => b.type === "trip")?.data || {};
  const clientBlock = blocks.find((b) => b.type === "client")?.data || {};
  const isBooking = data.proposal_type === "booking";

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#0A192F]">
      {/* PRINT-ONLY HEADER (logo + client + date) */}
      <PrintHeader proposal={data} />

      {/* HERO */}
      <Hero proposal={data} tripBlock={tripBlock} clientBlock={clientBlock} />

      {/* FLOATING PRINT ACTIONS — top-right, hidden when printing */}
      <PrintActions proposal={data} />

      {/* CONTENT GRID with sticky summary */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14" data-print-grid="true">
          {/* CONTENT */}
          <main className="lg:col-span-8 space-y-16 lg:space-y-24">
            {isBooking && (
              <div className="bg-[#0A192F] text-white p-6 lg:p-8" style={{ borderRadius: "4px" }} data-testid="booking-mode-banner">
                <p className="text-[#E8C97A] tracking-[0.3em] text-xs uppercase font-semibold mb-2">Propuesta lista para reservar</p>
                <p className="font-configurator text-2xl">Reserva cada elemento directamente con su proveedor oficial. Escapafy coordina y cuida cada detalle de tu viaje.</p>
              </div>
            )}
            {blocks.map((b) => (
              <BlockSection
                key={b.id}
                block={b}
                selections={selections}
                setSelections={setSelections}
                token={token}
                isBooking={isBooking}
              />
            ))}

            {/* Submit — hidden in booking mode */}
            {!isBooking && (
              <SubmitSection
                summary={summary}
                proposal={data}
                submitted={submitted}
                onRequest={() => setShowRequest(true)}
              />
            )}

            {isBooking && (
              <p className="text-center text-[#3A506B] text-xs italic max-w-2xl mx-auto leading-relaxed">
                La reserva se realiza directamente con el proveedor oficial para garantizar las mejores condiciones,
                políticas de cancelación y atención al cliente. Escapafy se encarga de la planificación y coordinación de tu viaje.
              </p>
            )}

            {/* PRINT-ONLY inline summary (replaces the sticky panel on paper) */}
            <PrintSummary proposal={data} summary={summary} selections={selections} />
          </main>

          {/* SUMMARY (desktop only inside the grid) */}
          <aside className="hidden lg:block lg:col-span-4">
            <div className="sticky top-10">
              <SummaryPanel
                proposal={data}
                summary={summary}
                selections={selections}
                onRequest={() => setShowRequest(true)}
                submitted={submitted}
                isBooking={isBooking}
              />
            </div>
          </aside>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* MOBILE bottom sticky bar */}
      <MobileSummaryBar
        summary={summary}
        proposal={data}
        selections={selections}
        expanded={mobileExpanded}
        onToggle={() => setMobileExpanded((v) => !v)}
        onRequest={() => { setMobileExpanded(false); setShowRequest(true); }}
        submitted={submitted}
        isBooking={isBooking}
      />

      {showRequest && (
        <RequestModal
          proposal={data}
          summary={summary}
          selections={selections}
          settings={settings}
          token={token}
          onClose={() => setShowRequest(false)}
          onSubmitted={() => { setSubmitted(true); setShowRequest(false); try { localStorage.removeItem(storageKey); } catch { /* noop */ } }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Pricing
// ─────────────────────────────────────────────────────────────────
function computeSummary(proposal, settings, selections) {
  if (!proposal) return { lines: [], total: 0, deposit: 0, ivaPct: 21, pricesIncludeIva: true, ivaAmount: 0, mgmtFee: 0, baseTotal: 0 };
  const blocks = (proposal.blocks || []).filter((b) => !b.hidden && b.type !== "private_notes");
  const lines = [];

  for (const b of blocks) {
    const items = b.data?.items || [];
    const mode = selectionModeOf(b);
    if (mode === "single" && SINGLE_SELECT_BLOCKS.includes(b.type)) {
      const sel = selections[b.type];
      const picked = items.find((it, idx) => itemId(it, idx) === sel);
      const amount = picked ? itemAmount(picked) : 0;
      if (items.length > 0) {
        lines.push({ blockType: b.type, label: BLOCK_TITLES[b.type], amount, name: picked?.name || picked?.airline || picked?.type || "" });
      }
    } else if (mode === "multi" && MULTI_SELECT_BLOCKS.includes(b.type)) {
      const sel = selections[b.type] || [];
      const picked = items.filter((it, idx) => sel.includes(itemId(it, idx)));
      const amount = picked.reduce((s, it) => s + itemAmount(it), 0);
      if (items.length > 0) {
        lines.push({ blockType: b.type, label: BLOCK_TITLES[b.type], amount, count: picked.length, items: picked });
      }
    } else if (mode === "informational") {
      // Informational blocks: not selectable but summed as fixed trip components
      // when items have amount + pricing_mode='included'
      let subtotal = 0;
      let countOnReq = 0;
      let countUnav = 0;
      let countIncluded = 0;
      for (const it of items) {
        const m = it.pricing_mode || (toNum(it.amount) > 0 ? "included" : "");
        if (m === "on_request") countOnReq++;
        else if (m === "unavailable") countUnav++;
        else if (m === "included") {
          countIncluded++;
          subtotal += toNum(it.amount);
        }
      }
      if (items.length > 0 && BLOCK_TITLES[b.type]) {
        lines.push({
          blockType: b.type,
          label: BLOCK_TITLES[b.type],
          amount: subtotal,
          informational: true,
          count: countIncluded,
          countOnReq,
          countUnav,
        });
      }
    } else if (b.type === "insurance") {
      const a = itemAmount(b.data || {});
      if (a > 0 || b.data?.company) {
        lines.push({ blockType: "insurance", label: BLOCK_TITLES.insurance, amount: a, name: b.data?.company || "" });
      }
    }
  }

  const baseTotal = lines.reduce((s, l) => s + (l.amount || 0), 0);
  const s = settings || {};
  const ivaPct = proposal.iva_pct_override != null ? Number(proposal.iva_pct_override) : Number(s.iva_pct ?? 21);
  const pricesIncludeIva = proposal.prices_include_iva_override != null ? !!proposal.prices_include_iva_override : (s.prices_include_iva !== false);
  const mgmtFeeKind = proposal.mgmt_fee_kind_override || s.mgmt_fee_kind || "none";
  const mgmtFeeValue = proposal.mgmt_fee_value_override != null ? toNum(proposal.mgmt_fee_value_override) : toNum(s.mgmt_fee_value);
  let mgmtFee = 0;
  if (mgmtFeeKind === "pct") mgmtFee = (baseTotal * mgmtFeeValue) / 100;
  else if (mgmtFeeKind === "fixed") mgmtFee = mgmtFeeValue;

  const preTax = baseTotal + mgmtFee;
  const ivaAmount = pricesIncludeIva ? (preTax - preTax / (1 + ivaPct / 100)) : (preTax * ivaPct / 100);
  const total = pricesIncludeIva ? preTax : preTax + ivaAmount;

  const depositKind = proposal.deposit_kind_override || s.deposit_kind || "none";
  const depositValue = proposal.deposit_value_override != null ? toNum(proposal.deposit_value_override) : toNum(s.deposit_value);
  let deposit = 0;
  if (depositKind === "pct") deposit = (total * depositValue) / 100;
  else if (depositKind === "fixed") deposit = depositValue;

  return { lines, baseTotal, mgmtFee, ivaPct, pricesIncludeIva, ivaAmount, total, deposit };
}

// ─────────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────────
function Hero({ proposal, tripBlock, clientBlock }) {
  const cover = proposal.cover_image;
  return (
    <section data-print-hero="true" className="relative bg-[#0A192F] text-[#FDFBF7] overflow-hidden">
      {cover && (
        <div data-print-hero-cover="true" className="absolute inset-0">
          <img src={cover} alt="" className="w-full h-full object-cover opacity-30" />
          <div data-print-hero-overlay="true" className="absolute inset-0 bg-gradient-to-b from-[#0A192F]/40 via-[#0A192F]/60 to-[#0A192F]"></div>
        </div>
      )}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-32">
        <p className="text-[#E8C97A] tracking-[0.3em] text-xs uppercase mb-6 font-medium">Propuesta personal · Escapafy</p>
        {tripBlock.destination_label || proposal.destination ? (
          <p className="font-configurator italic text-2xl text-[#E8C97A] mb-3">
            {tripBlock.destination_label || proposal.destination}
          </p>
        ) : null}
        <h1 className="font-configurator text-4xl sm:text-5xl lg:text-7xl leading-[1.05] text-white">
          {proposal.trip_title || "Tu viaje a medida"}
        </h1>
        {clientBlock.name || proposal.client_name ? (
          <p className="mt-6 text-white/70 max-w-xl text-sm sm:text-base font-light tracking-wide">
            Preparado para <span className="text-[#E8C97A]">{clientBlock.name || proposal.client_name}</span>.
            Personaliza cada elemento para construir tu experiencia ideal.
          </p>
        ) : null}
        {(proposal.date_from || proposal.date_to) && (
          <p className="mt-4 text-white/60 text-sm inline-flex items-center gap-2"><Calendar size={14} /> {proposal.date_from} {proposal.date_to ? `— ${proposal.date_to}` : ""}</p>
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// Section dispatch
// ─────────────────────────────────────────────────────────────────
function BlockSection({ block, selections, setSelections, token, isBooking = false }) {
  if (block.type === "client" || block.type === "trip") return null;

  const rawItems = block.data?.items || [];
  const mode = selectionModeOf(block);
  const Icon = BLOCK_ICONS[block.type] || Compass;
  const title = block.title?.trim() ? block.title : BLOCK_TITLES[block.type];
  const eyebrow = BLOCK_EYEBROWS[block.type];

  if (block.type === "insurance") return <InsuranceSection block={block} title={title} Icon={Icon} token={token} isBooking={isBooking} />;
  if (block.type === "extras") return <ExtrasSection block={block} title={title} Icon={Icon} />;
  if (block.type === "transport") return <InformationalListSection block={block} items={rawItems} title={title} Icon={Icon} token={token} isBooking={isBooking} />;

  // Booking mode: filter to items marked `is_chosen: true` for selectable blocks
  let items = rawItems;
  if (isBooking && SELECTABLE_BLOCKS.includes(block.type)) {
    const chosen = rawItems.filter((it) => it.is_chosen === true);
    if (chosen.length === 0) return null; // nothing chosen → hide block
    items = chosen;
    // In booking mode, render as informational cards with big Reservar buttons
    return <InformationalListSection block={block} items={items} title={title} Icon={Icon} token={token} isBooking={true} />;
  }

  if (items.length === 0) return null;

  // Single
  if (mode === "single" && SINGLE_SELECT_BLOCKS.includes(block.type)) {
    const sel = selections[block.type];
    const toggle = (id) => setSelections((s) => ({ ...s, [block.type]: s[block.type] === id ? null : id }));
    return (
      <section data-testid={`section-${block.type}`}>
        <SectionHeader eyebrow={eyebrow} title={title} Icon={Icon} count={items.length} />
        <div className={
          block.type === "transfers"
            ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5"
            : "grid grid-cols-1 lg:grid-cols-2 gap-5"
        }>
          {items.map((it, idx) => {
            const id = itemId(it, idx);
            const selected = sel === id;
            const cardProps = { item: it, selected, onSelect: () => toggle(id), index: idx, token, blockType: block.type };
            if (block.type === "flights") return <FlightOptionCard key={id} {...cardProps} />;
            if (block.type === "hotels") return <HotelOptionCard key={id} {...cardProps} />;
            if (block.type === "transfers") return <TransferOptionCard key={id} {...cardProps} />;
            return null;
          })}
        </div>
      </section>
    );
  }

  // Multi
  if (mode === "multi" && MULTI_SELECT_BLOCKS.includes(block.type)) {
    const list = selections[block.type] || [];
    const toggle = (id) => setSelections((s) => {
      const arr = s[block.type] || [];
      return { ...s, [block.type]: arr.includes(id) ? arr.filter((x) => x !== id) : arr.concat([id]) };
    });
    return (
      <section data-testid={`section-${block.type}`}>
        <SectionHeader eyebrow={eyebrow} title={title} Icon={Icon} count={items.length} />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((it, idx) => {
            const id = itemId(it, idx);
            const selected = list.includes(id);
            return (
              <MultiSelectCard
                key={id}
                item={it}
                selected={selected}
                onSelect={() => toggle(id)}
                blockType={block.type}
                token={token}
              />
            );
          })}
        </div>
      </section>
    );
  }

  return <InformationalListSection block={block} items={items} title={title} Icon={Icon} token={token} />;
}

function SectionHeader({ eyebrow, title, Icon, count }) {
  return (
    <header className="mb-8 lg:mb-10">
      {eyebrow && <p className="text-[#E8C97A] tracking-[0.25em] text-xs uppercase mb-3 font-semibold">{eyebrow}</p>}
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <h2 className="font-configurator text-3xl lg:text-5xl text-[#0A192F]">{title}</h2>
        {count > 1 && <span className="text-[#3A506B] text-sm italic">{count} opciones</span>}
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────────
// Image carousel
// ─────────────────────────────────────────────────────────────────
function ImageCarousel({ images, alt, className = "" }) {
  const [idx, setIdx] = useState(0);
  if (!images.length) {
    return (
      <div className={`bg-[#F3EFE7] flex items-center justify-center ${className}`}>
        <span className="text-[#3A506B]/40 text-xs">Sin imagen</span>
      </div>
    );
  }
  const next = (e) => { e?.stopPropagation(); setIdx((i) => (i + 1) % images.length); };
  const prev = (e) => { e?.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); };
  return (
    <div data-carousel="true" className={`relative overflow-hidden group ${className}`}>
      {images.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={alt}
          data-carousel-image={i}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out ${i === idx ? "opacity-100" : "opacity-0"}`}
        />
      ))}
      {images.length > 1 && (
        <div data-carousel-controls="true">
          <button
            type="button" onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 bg-white/70 backdrop-blur-md text-[#0A192F] p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            aria-label="Anterior"
          ><ChevronLeft size={16} /></button>
          <button
            type="button" onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 bg-white/70 backdrop-blur-md text-[#0A192F] p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            aria-label="Siguiente"
          ><ChevronRight size={16} /></button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                className={`h-1 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/50"}`}
                aria-label={`Imagen ${i + 1}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Selection indicator (luxury check)
// ─────────────────────────────────────────────────────────────────
function SelectionBadge({ selected }) {
  return (
    <div
      className={`absolute top-4 right-4 z-10 flex items-center justify-center w-9 h-9 rounded-full transition-all duration-300 ${
        selected
          ? "bg-[#E8C97A] text-[#0A192F] scale-110 shadow-[0_4px_16px_rgba(232,201,122,0.4)]"
          : "bg-white/80 backdrop-blur-md border border-[#EAE3D5] text-transparent"
      }`}
    >
      <Check size={16} strokeWidth={2.5} />
    </div>
  );
}

const cardBase = "relative overflow-hidden bg-[#FDFBF7] border transition-all duration-300 ease-out cursor-pointer";
const cardDefault = "border-[#EAE3D5] hover:border-[#D8CFBC] hover:shadow-[0_12px_40px_rgba(10,25,47,0.06)] hover:-translate-y-[2px]";
const cardSelected = "border-[#E8C97A] ring-1 ring-[#E8C97A] shadow-[0_8px_28px_rgba(232,201,122,0.18)] bg-white";

// ─────────────────────────────────────────────────────────────────
// Flight option card (round-trip)
// ─────────────────────────────────────────────────────────────────
function FlightOptionCard({ item, selected, onSelect, index, token, blockType }) {
  const opt = `Opción ${index + 1}`;
  const name = item.name || opt;
  const price = itemAmount(item);
  const mode = item.pricing_mode || (price > 0 ? "included" : "");
  const onRequest = mode === "on_request" || mode === "unavailable";

  return (
    <article
      onClick={onSelect}
      data-testid="flight-option-card"
      data-selectable="true"
      data-selected={selected ? "true" : "false"}
      className={`${cardBase} ${selected ? cardSelected : cardDefault} p-6 lg:p-8 flex flex-col h-full`}
      style={{ borderRadius: "4px" }}
    >
      <SelectionBadge selected={selected} />
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-5">
        <div className="min-w-0 flex-1">
          <p className="text-[#E8C97A] text-[0.65rem] tracking-[0.3em] uppercase font-semibold mb-1">{opt}</p>
          <h3 className="font-configurator text-2xl text-[#0A192F] truncate">{name}</h3>
        </div>
        <div className="text-right">
          {onRequest ? (
            <p className="font-configurator text-xl italic text-[#3A506B]">Bajo petición</p>
          ) : (
            <>
              <p className="text-[#3A506B] text-[0.65rem] tracking-[0.2em] uppercase">Ida + vuelta</p>
              <p className="font-configurator text-3xl text-[#0A192F] font-light">{price > 0 ? fmt(price) : (item.price || "—")}</p>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 mt-2">
        <FlightLeg leg="outbound" label="Ida" item={item} icon={ArrowRight} />
        <FlightLeg leg="return" label="Vuelta" item={item} icon={ArrowDownRight} />
      </div>

      {(item.baggage || item.notes) && (
        <div className="mt-5 pt-5 border-t border-[#EAE3D5] flex flex-wrap items-center gap-4 text-sm text-[#3A506B]">
          {item.baggage && <span className="inline-flex items-center gap-2"><Sparkles size={13} className="text-[#E8C97A]" /> {item.baggage}</span>}
          {item.notes && <span className="italic">{item.notes}</span>}
        </div>
      )}

      <div className="mt-auto pt-5 border-t border-[#EAE3D5] flex items-center justify-between">
        <p className={`font-configurator italic text-sm transition-opacity ${selected ? "text-[#E8C97A] opacity-100" : "text-[#3A506B] opacity-70"}`}>
          {selected ? "Esta es tu elección" : `Seleccionar ${opt}`}
        </p>
        {item.affiliate_url && (
          <AffiliateBtn url={item.affiliate_url} token={token} blockType={blockType} itemName={name} />
        )}
      </div>
    </article>
  );
}

function FlightLeg({ leg, label, item, icon: Icon }) {
  const g = (k) => item[`${leg}_${k}`];
  return (
    <div>
      <p className="inline-flex items-center gap-2 text-[#E8C97A] tracking-[0.25em] text-[0.65rem] uppercase font-semibold mb-3">
        <Icon size={11} /> {label}
      </p>
      <p className="font-configurator text-xl text-[#0A192F]">{g("airline") || "—"} {g("number") ? <span className="text-[#3A506B] text-sm">· {g("number")}</span> : null}</p>
      <p className="text-[#3A506B] text-sm mt-1">{g("route") || ""}</p>
      <div className="mt-4 flex items-center gap-4 text-sm text-[#0A192F]">
        <div>
          <p className="font-light text-2xl">{g("departure") || "—"}</p>
          <p className="text-xs text-[#3A506B]">{g("date") || ""}</p>
        </div>
        <div className="flex-1 border-t border-dashed border-[#EAE3D5] relative">
          {g("duration") && (
            <span className="absolute left-1/2 -translate-x-1/2 -top-3 bg-[#FDFBF7] px-2 text-[0.65rem] text-[#3A506B] tracking-wider inline-flex items-center gap-1">
              <Clock size={10} /> {g("duration")}
            </span>
          )}
        </div>
        <div className="text-right">
          <p className="font-light text-2xl">{g("arrival") || "—"}</p>
          <p className="text-xs text-[#3A506B]">{g("stops") || "Directo"}</p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Hotel option card
// ─────────────────────────────────────────────────────────────────
function HotelOptionCard({ item, selected, onSelect, token, blockType }) {
  const imgs = itemImages(item);
  const price = itemAmount(item);
  const mode = item.pricing_mode || (price > 0 ? "included" : "");
  const onRequest = mode === "on_request" || mode === "unavailable";

  return (
    <article
      onClick={onSelect}
      data-testid="hotel-option-card"
      data-selectable="true"
      data-selected={selected ? "true" : "false"}
      className={`${cardBase} ${selected ? cardSelected : cardDefault} flex flex-col h-full`}
      style={{ borderRadius: "4px" }}
    >
      <SelectionBadge selected={selected} />
      <ImageCarousel images={imgs} alt={item.name || "Hotel"} className="w-full aspect-[16/10] shrink-0" />
      <div className="flex-1 p-6 lg:p-7 flex flex-col">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            {item.category && <p className="text-[#E8C97A] text-[0.65rem] tracking-[0.3em] uppercase font-semibold mb-1">{item.category}</p>}
            <h3 className="font-configurator text-2xl text-[#0A192F]">{item.name || "Hotel"}</h3>
            {item.location && <p className="text-[#3A506B] text-sm mt-1 inline-flex items-center gap-1.5"><MapPin size={12} /> {item.location || item.address}</p>}
            {item.rating && <p className="text-[#3A506B] text-xs mt-1 italic">{item.rating}</p>}
          </div>
          {!onRequest && price > 0 && (
            <div className="text-right">
              <p className="text-[#3A506B] text-[0.65rem] tracking-[0.2em] uppercase">{item.nights ? `${item.nights} noches` : "Total"}</p>
              <p className="font-configurator text-2xl text-[#0A192F] font-light">{fmt(price)}</p>
              {item.price_per_night && <p className="text-xs text-[#3A506B]">{item.price_per_night}/noche</p>}
            </div>
          )}
        </div>

        {item.description && <p className="text-[#3A506B] text-sm mt-4 leading-relaxed whitespace-pre-wrap">{item.description}</p>}

        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-[#3A506B]">
          {item.room_type && <span>· {item.room_type}</span>}
          {item.board && <span>· {item.board}</span>}
          {item.services && <span className="italic whitespace-pre-wrap">· {item.services}</span>}
        </div>

        <div className="mt-auto pt-5 border-t border-[#EAE3D5] flex items-center justify-between">
          <p className={`font-configurator italic text-sm transition-opacity ${selected ? "text-[#E8C97A] opacity-100" : "text-[#3A506B] opacity-70"}`}>
            {selected ? "Hotel elegido" : "Elegir este hotel"}
          </p>
          {item.affiliate_url && <AffiliateBtn url={item.affiliate_url} token={token} blockType={blockType} itemName={item.name} />}
        </div>
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────
// Transfer option card (compact)
// ─────────────────────────────────────────────────────────────────
function TransferOptionCard({ item, selected, onSelect, token, blockType }) {
  const imgs = itemImages(item);
  const price = itemAmount(item);
  const mode = item.pricing_mode || (price > 0 ? "included" : "");
  const onRequest = mode === "on_request" || mode === "unavailable";

  return (
    <article
      onClick={onSelect}
      data-testid="transfer-option-card"
      data-selectable="true"
      data-selected={selected ? "true" : "false"}
      className={`${cardBase} ${selected ? cardSelected : cardDefault} flex flex-col h-full`}
      style={{ borderRadius: "4px" }}
    >
      <SelectionBadge selected={selected} />
      {imgs.length > 0 && (
        <ImageCarousel images={imgs} alt={item.type || "Traslado"} className="w-full aspect-[16/10] shrink-0" />
      )}
      <div className="flex-1 p-5 lg:p-6 flex flex-col">
        <h3 className="font-configurator text-xl text-[#0A192F]">{item.type || "Traslado"}</h3>
        {item.company && <p className="text-[#3A506B] text-xs mt-1">{item.company}</p>}
        {item.description && <p className="text-[#3A506B] text-sm mt-3 whitespace-pre-wrap leading-relaxed">{item.description}</p>}
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#3A506B]">
          {item.duration && <span className="inline-flex items-center gap-1"><Clock size={11} /> {item.duration}</span>}
          {item.schedule && <span>· {item.schedule}</span>}
        </div>
        <div className="mt-auto pt-4 border-t border-[#EAE3D5] flex items-center justify-between">
          <p className={`font-configurator italic text-sm ${selected ? "text-[#E8C97A]" : "text-[#3A506B]"}`}>
            {selected ? "Seleccionado" : "Seleccionar"}
          </p>
          {onRequest ? (
            <p className="font-configurator italic text-[#3A506B] text-sm">Bajo petición</p>
          ) : (
            <p className="font-configurator text-xl text-[#0A192F] font-light">{price > 0 ? fmt(price) : (item.price || "—")}</p>
          )}
        </div>
        {item.affiliate_url && (
          <div className="mt-3">
            <AffiliateBtn url={item.affiliate_url} token={token} blockType={blockType} itemName={item.type} />
          </div>
        )}
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────
// Multi-select card (restaurants, activities, excursions)
// ─────────────────────────────────────────────────────────────────
function MultiSelectCard({ item, selected, onSelect, blockType, token }) {
  const imgs = itemImages(item);
  const price = itemAmount(item);
  const mode = item.pricing_mode || (price > 0 ? "included" : "");
  const isFav = blockType === "restaurants";
  const ctaIdle = isFav ? "Añadir a favoritos" : "Añadir al viaje";
  const ctaActive = isFav ? "En favoritos" : "En tu viaje";

  return (
    <article
      onClick={onSelect}
      data-testid={`${({restaurants:"restaurant",activities:"activity",excursions:"excursion"}[blockType] || blockType)}-option-card`}
      data-selectable="true"
      data-selected={selected ? "true" : "false"}
      className={`${cardBase} ${selected ? cardSelected : cardDefault}`}
      style={{ borderRadius: "4px" }}
    >
      <SelectionBadge selected={selected} />
      <ImageCarousel images={imgs} alt={item.name || ""} className="w-full aspect-[4/3]" />
      <div className="p-6 lg:p-7">
        {item.cuisine && <p className="text-[#E8C97A] text-[0.65rem] tracking-[0.3em] uppercase font-semibold mb-1">{item.cuisine}</p>}
        <h3 className="font-configurator text-xl lg:text-2xl text-[#0A192F]">{item.name || "Sin nombre"}</h3>
        {item.address && <p className="text-[#3A506B] text-xs mt-1 inline-flex items-center gap-1"><MapPin size={11} /> {item.address}</p>}
        {item.duration && <p className="text-[#3A506B] text-xs mt-1 inline-flex items-center gap-1"><Clock size={11} /> {item.duration}</p>}
        {item.description && <p className="text-[#3A506B] text-sm mt-3 leading-relaxed whitespace-pre-wrap">{item.description}</p>}
        {item.includes && <p className="text-[#3A506B] text-xs mt-2 italic">Incluye: {item.includes}</p>}
        <div className="mt-5 pt-5 border-t border-[#EAE3D5] flex items-center justify-between">
          <p className={`font-configurator italic text-sm ${selected ? "text-[#E8C97A]" : "text-[#3A506B]"}`}>
            {selected ? ctaActive : ctaIdle}
          </p>
          <div className="text-right">
            {mode === "on_request" ? (
              <p className="font-configurator italic text-sm text-[#3A506B]">Bajo petición</p>
            ) : price > 0 ? (
              <p className="font-configurator text-lg text-[#0A192F] font-light">{fmt(price)}</p>
            ) : item.price_avg || item.price ? (
              <p className="text-sm text-[#3A506B]">{item.price_avg || item.price}</p>
            ) : null}
            {item.affiliate_url && (
              <div className="mt-2">
                <AffiliateBtn url={item.affiliate_url} token={token} blockType={blockType} itemName={item.name} />
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────
// Insurance / Extras / Informational
// ─────────────────────────────────────────────────────────────────
function InsuranceSection({ block, title, Icon, token, isBooking = false }) {
  const d = block.data || {};
  // Show the section if there is ANY meaningful content — company, coverage,
  // price/amount OR an affiliate link (so pasting just the URL is enough to
  // render the CTA in the booking proposal).
  if (!d.company && !d.coverage && !d.price && !d.amount && !d.affiliate_url) return null;
  const price = toNum(d.amount);
  return (
    <section data-testid="section-insurance">
      <SectionHeader title={title} Icon={Icon} eyebrow="Tranquilidad" />
      <div className="bg-[#FDFBF7] border border-[#EAE3D5] p-7 lg:p-9" style={{ borderRadius: "4px" }}>
        {d.company ? (
          <h3 className="font-configurator text-2xl text-[#0A192F]">{d.company}</h3>
        ) : d.affiliate_url ? (
          <h3 className="font-configurator text-2xl text-[#0A192F]">Seguro de viaje</h3>
        ) : null}
        {d.coverage && <p className="text-[#3A506B] text-sm mt-3 whitespace-pre-wrap leading-relaxed">{d.coverage}</p>}
        {(price > 0 || (d.price || "").trim()) && (
          <p className="font-configurator text-xl text-[#0A192F] mt-4">{price > 0 ? fmt(price) : d.price}</p>
        )}
        {d.affiliate_url && (
          <div className="mt-5 pt-5 border-t border-[#EAE3D5]">
            <AffiliateBtn url={d.affiliate_url} token={token} blockType="insurance" itemName={d.company || "Seguro de viaje"} itemId="insurance" big={isBooking} />
          </div>
        )}
      </div>
    </section>
  );
}

function ExtrasSection({ block, title, Icon }) {
  const d = block.data || {};
  const rows = [
    ["Clima", d.weather], ["Documentación", d.documents], ["Moneda", d.currency],
    ["Enchufes", d.plugs], ["Internet / SIM", d.internet], ["Idioma", d.language],
  ].filter(([_, v]) => v);
  if (!rows.length && !d.tips && !d.what_to_bring && !d.personal_recommendations) return null;
  return (
    <section data-testid="section-extras">
      <SectionHeader title={title} Icon={Icon} eyebrow="Lo que conviene saber" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
        {rows.map(([k, v]) => (
          <div key={k} className="border-b border-[#EAE3D5] pb-3">
            <p className="text-[0.65rem] tracking-[0.2em] uppercase text-[#3A506B] mb-1">{k}</p>
            <p className="text-[#0A192F]">{v}</p>
          </div>
        ))}
      </div>
      {d.tips && <ParagraphCard title="Consejos" text={d.tips} />}
      {d.what_to_bring && <ParagraphCard title="Qué llevar" text={d.what_to_bring} />}
      {d.personal_recommendations && <ParagraphCard title="Recomendaciones" text={d.personal_recommendations} />}
    </section>
  );
}

function ParagraphCard({ title, text }) {
  return (
    <div className="mt-6 bg-[#F3EFE7] p-6 border border-[#EAE3D5]" style={{ borderRadius: "4px" }}>
      <p className="font-configurator text-xl text-[#0A192F] mb-2">{title}</p>
      <p className="text-[#3A506B] whitespace-pre-wrap leading-relaxed text-sm">{text}</p>
    </div>
  );
}

function InformationalListSection({ block, items, title, Icon, token, isBooking = false }) {
  if (items.length === 0) return null;
  return (
    <section data-testid={`section-${block.type}`}>
      <SectionHeader title={title} Icon={Icon} eyebrow={isBooking ? "Tu elección para reservar" : BLOCK_EYEBROWS[block.type]} count={isBooking ? 0 : items.length} />
      <div className={items.length > 1 ? "grid grid-cols-1 lg:grid-cols-2 gap-5" : "space-y-5"}>
        {items.map((it, idx) => {
          const imgs = itemImages(it);
          const price = itemAmount(it);
          const mode = it.pricing_mode || (price > 0 ? "included" : "");
          const onRequest = mode === "on_request" || mode === "unavailable";
          return (
            <article
              key={itemId(it, idx)}
              className="bg-[#FDFBF7] border border-[#EAE3D5] overflow-hidden flex flex-col h-full"
              style={{ borderRadius: "4px" }}
              data-testid={`info-item-${block.type}`}
            >
              {imgs.length > 0 && (
                <ImageCarousel images={imgs} alt={it.name || ""} className="w-full aspect-[16/10] shrink-0" />
              )}
              <div className="flex-1 p-5 lg:p-6 flex flex-col">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <h3 className="font-configurator text-xl lg:text-2xl text-[#0A192F]">
                      {it.name || it.airline || it.company || it.type || "Sin nombre"}
                    </h3>
                    {(it.address || it.location) && (
                      <p className="text-[#3A506B] text-xs mt-1 inline-flex items-center gap-1.5">
                        <MapPin size={11} /> {it.location || it.address}
                      </p>
                    )}
                    {it.duration && (
                      <p className="text-[#3A506B] text-xs mt-1 inline-flex items-center gap-1.5">
                        <Clock size={11} /> {it.duration}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    {onRequest ? (
                      <p className="font-configurator italic text-[#3A506B]">Bajo petición</p>
                    ) : price > 0 ? (
                      <p className="font-configurator text-2xl text-[#0A192F] font-light">{fmt(price)}</p>
                    ) : it.price ? (
                      <p className="font-configurator text-lg text-[#0A192F]">{it.price}</p>
                    ) : null}
                  </div>
                </div>
                {it.description && (
                  <p className="text-[#3A506B] text-sm mt-3 leading-relaxed whitespace-pre-wrap">{it.description}</p>
                )}
                {it.includes && (
                  <p className="text-[#3A506B] text-xs mt-2 italic whitespace-pre-wrap">Incluye: {it.includes}</p>
                )}
                {(it.affiliate_url || it.map_url) && (
                  <div className="mt-auto pt-5 border-t border-[#EAE3D5] flex flex-wrap gap-3 items-center">
                    {it.affiliate_url ? (
                      <AffiliateBtn
                        url={it.affiliate_url}
                        token={token}
                        blockType={block.type}
                        itemName={it.name || it.airline || it.type}
                        itemId={itemId(it, idx)}
                        big={isBooking}
                      />
                    ) : isBooking ? (
                      <span className="text-xs italic text-[#3A506B]">Enlace de reserva pendiente — contacta con nosotros para reservar.</span>
                    ) : null}
                    {it.map_url && (
                      <a href={it.map_url} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1.5 border border-[#EAE3D5] hover:border-[#0A192F] text-[#0A192F] text-xs tracking-wider px-4 py-2 transition-colors"
                         style={{ borderRadius: "2px" }}
                      >
                        Ver mapa <ArrowUpRight size={11} />
                      </a>
                    )}
                  </div>
                )}
                {isBooking && !it.affiliate_url && !it.map_url && (
                  <div className="mt-auto pt-5 border-t border-[#EAE3D5]">
                    <span className="text-xs italic text-[#3A506B]">Enlace de reserva pendiente — contacta con nosotros para reservar.</span>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// Affiliate link button
// ─────────────────────────────────────────────────────────────────
function AffiliateBtn({ url, token, blockType, itemName, itemId: itemIdProp, big = false }) {
  if (!url) return null;
  // Booking mode ("Reservar ahora") → route through the premium intermediate
  // /reserva/:token confirmation page in the SAME tab, then open provider in a new tab.
  if (big) {
    const params = new URLSearchParams();
    if (blockType) params.set("block", blockType);
    if (itemIdProp) params.set("item", itemIdProp);
    const reservaHref = `/reserva/${encodeURIComponent(token || "")}${params.toString() ? `?${params.toString()}` : ""}`;
    return (
      <a
        href={reservaHref}
        onClick={(e) => e.stopPropagation()}
        data-testid="book-now-btn"
        className="inline-flex items-center justify-center gap-2 bg-[#E8C97A] hover:bg-[#D4B663] text-[#0A192F] px-7 py-4 text-sm tracking-[0.2em] uppercase font-bold transition-colors shadow-[0_8px_24px_rgba(232,201,122,0.28)]"
        style={{ borderRadius: "2px" }}
      >
        Reservar ahora <ArrowUpRight size={14} />
      </a>
    );
  }
  // Informative mode ("Ver detalle") → keep quick affiliate bridge, opens in new tab
  const bridgeUrl = `/go?url=${encodeURIComponent(url)}&token=${encodeURIComponent(token || "")}&block=${encodeURIComponent(blockType || "")}&item=${encodeURIComponent(itemName || "")}`;
  return (
    <a
      href={bridgeUrl}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1.5 border border-[#EAE3D5] hover:border-[#0A192F] text-[#0A192F] text-xs tracking-wider px-4 py-2 transition-colors"
      style={{ borderRadius: "2px" }}
    >
      Ver detalle <ArrowUpRight size={11} />
    </a>
  );
}

// ─────────────────────────────────────────────────────────────────
// Summary panel
// ─────────────────────────────────────────────────────────────────
function SummaryPanel({ proposal, summary, selections, onRequest, submitted, isBooking = false }) {
  const hasUserPick =
    summary.lines.some((l) => l.amount > 0 && l.blockType !== "insurance") ||
    summary.lines.some((l) => (l.count || 0) > 0);
  return (
    <aside
      data-testid="live-summary-panel"
      className="bg-[#FDFBF7] border border-[#EAE3D5] p-7 lg:p-8 shadow-[0_12px_40px_rgba(10,25,47,0.04)]"
      style={{ borderRadius: "4px" }}
    >
      <p className="text-[#E8C97A] text-[0.65rem] tracking-[0.3em] uppercase font-semibold mb-3">{isBooking ? "Coste estimado" : "Tu viaje"}</p>
      <h3 className="font-configurator text-2xl text-[#0A192F]">{isBooking ? "Coste total del viaje" : "Resumen vivo"}</h3>
      <p className="text-[#3A506B] text-xs mt-1 italic">{isBooking ? "Referencia orientativa. Cada elemento se reserva directamente con su proveedor." : "Se actualiza con cada elección"}</p>

      <div className="mt-6 space-y-4">
        {summary.lines.length === 0 && (
          <div className="bg-[#F3EFE7] border border-dashed border-[#EAE3D5] p-6 text-center" style={{ borderRadius: "2px" }}>
            <p className="font-configurator italic text-[#3A506B]">Selecciona tus opciones para construir tu viaje.</p>
          </div>
        )}
        {summary.lines.map((l) => (
          <div key={l.blockType} className="flex items-baseline justify-between gap-4 py-3 border-b border-[#EAE3D5] last:border-0 text-sm">
            <div className="flex-1 min-w-0">
              <p className="text-[#0A192F] font-medium">{l.label}</p>
              {l.name && <p className="text-[#3A506B] text-xs truncate">{l.name}</p>}
              {l.count != null && <p className="text-[#3A506B] text-xs">{l.count} {l.informational ? (l.count === 1 ? "incluido" : "incluidos") : (l.count === 1 ? "seleccionado" : "seleccionados")}</p>}
            </div>
            <span className="font-configurator text-base text-[#0A192F] font-light">{l.amount > 0 ? fmt(l.amount) : "—"}</span>
          </div>
        ))}
      </div>

      <div className="mt-6 pt-6 border-t border-[#0A192F]/10">
        <div className="flex items-baseline justify-between">
          <p className="font-configurator text-xl text-[#0A192F]">Total</p>
          <p className="font-configurator text-4xl text-[#0A192F] font-light tracking-tight">{fmt(summary.total)}</p>
        </div>
        {summary.deposit > 0 && (
          <p className="text-xs text-[#3A506B] mt-2">Señal de {fmt(summary.deposit)} para confirmar.</p>
        )}
        {summary.ivaPct > 0 && summary.pricesIncludeIva && (
          <p className="text-[0.65rem] text-[#3A506B]/70 mt-1">IVA del {summary.ivaPct}% incluido</p>
        )}
      </div>

      {submitted ? (
        <div className="mt-7 bg-[#0A192F] text-[#FDFBF7] p-5 text-center" style={{ borderRadius: "2px" }}>
          <p className="font-configurator italic text-[#E8C97A]">Solicitud enviada.</p>
          <p className="text-xs text-white/70 mt-1">Nos pondremos en contacto contigo en breve.</p>
        </div>
      ) : isBooking ? (
        <p className="mt-6 text-[#3A506B] text-xs italic">
          Pulsa <strong className="text-[#0A192F]">«Reservar ahora»</strong> en cada elemento del viaje para completar tu reserva con el proveedor oficial.
        </p>
      ) : (
        <button
          type="button"
          onClick={onRequest}
          data-testid="submit-proposal-btn"
          disabled={!hasUserPick}
          className="mt-7 w-full bg-[#0A192F] hover:bg-[#16335A] disabled:opacity-40 disabled:cursor-not-allowed text-[#FDFBF7] px-6 py-4 text-sm tracking-[0.15em] uppercase font-medium transition-colors inline-flex items-center justify-center gap-2"
          style={{ borderRadius: "2px" }}
        >
          Solicitar esta propuesta <ArrowRight size={14} />
        </button>
      )}
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────
// Mobile sticky bar
// ─────────────────────────────────────────────────────────────────
function MobileSummaryBar({ summary, proposal, selections, expanded, onToggle, onRequest, submitted, isBooking = false }) {
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        data-testid="mobile-summary-toggle"
        className="lg:hidden fixed bottom-4 left-4 right-4 z-40 bg-[#0A192F] text-[#FDFBF7] px-6 py-4 flex items-center justify-between shadow-[0_20px_40px_rgba(10,25,47,0.25)]"
        style={{ borderRadius: "2px" }}
      >
        <span className="text-xs tracking-[0.2em] uppercase">{expanded ? "Cerrar resumen" : (isBooking ? "Coste estimado" : "Ver tu viaje")}</span>
        <span className="font-configurator text-2xl font-light">{fmt(summary.total)}</span>
      </button>
      {expanded && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 bg-[#FDFBF7] p-6 shadow-[0_-20px_40px_rgba(0,0,0,0.15)] z-50 max-h-[85vh] overflow-y-auto" style={{ borderTopLeftRadius: "16px", borderTopRightRadius: "16px" }}>
          <div className="w-12 h-1 bg-[#EAE3D5] rounded-full mx-auto mb-5"></div>
          <SummaryPanel proposal={proposal} summary={summary} selections={selections} onRequest={onRequest} submitted={submitted} isBooking={isBooking} />
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// Submit section (in-flow CTA, complements sticky)
// ─────────────────────────────────────────────────────────────────
function SubmitSection({ summary, proposal, submitted, onRequest }) {
  // Disable if no real client selection was made (insurance preselected by admin doesn't count)
  const hasUserPick =
    summary.lines.some((l) => l.amount > 0 && l.blockType !== "insurance") ||
    summary.lines.some((l) => (l.count || 0) > 0);
  return (
    <section data-print-cta="true" className="bg-[#0A192F] text-[#FDFBF7] p-10 lg:p-14 text-center relative overflow-hidden" style={{ borderRadius: "4px" }}>
      <p className="text-[#E8C97A] text-[0.65rem] tracking-[0.3em] uppercase font-semibold mb-4">Último paso</p>
      <h2 className="font-configurator text-4xl lg:text-5xl text-white">Confirmemos tu viaje.</h2>
      <p className="text-white/70 max-w-md mx-auto mt-4 text-sm leading-relaxed">
        Cuando lo tengas claro, envíanos tu configuración y nos encargamos del resto.
        Te confirmamos disponibilidad real en menos de 24 horas.
      </p>
      <p className="font-configurator text-5xl lg:text-6xl text-[#E8C97A] mt-8 font-light">{fmt(summary.total)}</p>
      {summary.deposit > 0 && <p className="text-white/60 text-xs mt-2">+ {fmt(summary.deposit)} de señal al confirmar</p>}
      {submitted ? (
        <p className="mt-8 font-configurator italic text-[#E8C97A] text-xl">Solicitud enviada · te contactamos en breve</p>
      ) : (
        <button
          type="button"
          onClick={onRequest}
          disabled={!hasUserPick}
          data-testid="submit-proposal-btn-inline"
          className="mt-10 bg-[#E8C97A] hover:bg-[#D4B663] disabled:opacity-40 disabled:cursor-not-allowed text-[#0A192F] px-10 py-5 text-sm tracking-[0.2em] uppercase font-bold inline-flex items-center gap-3 transition-colors"
          style={{ borderRadius: "2px" }}
        >
          Solicitar esta propuesta <ArrowRight size={14} />
        </button>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// Request modal
// ─────────────────────────────────────────────────────────────────
function RequestModal({ proposal, summary, selections, settings, token, onClose, onSubmitted }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const terms = settings?.accept_form_terms || "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/public/proposals/${token}/request`, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        notes: notes.trim() || undefined,
        terms_accepted: !!acceptTerms,
        selections,
        computed_total: summary.total,
      });
      onSubmitted();
    } catch (err) {
      setError(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#0A192F]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="request-modal">
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FDFBF7] max-w-xl w-full max-h-[92vh] overflow-y-auto p-8 lg:p-10 space-y-5"
        style={{ borderRadius: "4px" }}
      >
        <div>
          <p className="text-[#E8C97A] text-[0.65rem] tracking-[0.3em] uppercase font-semibold mb-2">Solicitar propuesta</p>
          <h2 className="font-configurator text-3xl text-[#0A192F]">Casi listo</h2>
          <p className="text-[#3A506B] text-sm mt-2">Déjanos tus datos y te contactamos para confirmar.</p>
        </div>

        <div className="bg-[#F3EFE7] p-4 border border-[#EAE3D5]" style={{ borderRadius: "2px" }}>
          <p className="text-[#3A506B] text-xs">Total estimado</p>
          <p className="font-configurator text-3xl text-[#0A192F] font-light">{fmt(summary.total)}</p>
        </div>

        <Input label="Nombre completo *" value={name} onChange={setName} required testid="req-name" />
        <Input label="Email *" type="email" value={email} onChange={setEmail} required testid="req-email" />
        <Input label="Teléfono (opcional)" value={phone} onChange={setPhone} testid="req-phone" />
        <label className="block">
          <span className="block text-[0.65rem] font-medium text-[#0A192F] mb-2 uppercase tracking-[0.2em]">Comentarios</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="req-notes"
            className="w-full px-4 py-3 bg-white border border-[#EAE3D5] focus:border-[#0A192F] focus:outline-none text-sm font-sans"
            style={{ borderRadius: "2px" }}
          />
        </label>
        {terms && (
          <label className="flex items-start gap-2.5 text-xs text-[#3A506B]">
            <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} required className="mt-1 accent-[#0A192F]" data-testid="req-terms" />
            <span className="leading-relaxed">{terms}</span>
          </label>
        )}
        {error && <p className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 text-sm">{error}</p>}
        <div className="flex gap-3 justify-end pt-2">
          <button type="button" onClick={onClose} className="px-5 py-3 text-sm text-[#3A506B] hover:text-[#0A192F]">Cancelar</button>
          <button type="submit"
            disabled={submitting || (terms && !acceptTerms)}
            data-testid="req-submit"
            className="bg-[#0A192F] hover:bg-[#16335A] disabled:opacity-50 text-white px-7 py-4 text-sm tracking-[0.15em] uppercase font-medium inline-flex items-center gap-2"
            style={{ borderRadius: "2px" }}
          >
            {submitting ? "Enviando..." : (<>Enviar solicitud <Send size={13} /></>)}
          </button>
        </div>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="block text-[0.65rem] font-medium text-[#0A192F] mb-2 uppercase tracking-[0.2em]">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full px-4 py-3 bg-white border border-[#EAE3D5] focus:border-[#0A192F] focus:outline-none text-sm font-sans"
        style={{ borderRadius: "2px" }}
      />
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────
// Password gate + utilities
// ─────────────────────────────────────────────────────────────────
function PasswordGate({ pwd, setPwd, onSubmit, error }) {
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="max-w-md w-full bg-white border border-[#EAE3D5] p-10" style={{ borderRadius: "4px" }}>
        <Lock className="text-[#E8C97A] mb-5" size={32} />
        <h1 className="font-configurator text-3xl text-[#0A192F]">Propuesta privada</h1>
        <p className="text-[#3A506B] text-sm mt-2 mb-6">Introduce la contraseña para acceder.</p>
        <Input label="Contraseña" type="password" value={pwd} onChange={setPwd} required testid="gate-pwd" />
        {error && <p className="text-red-700 text-sm mt-3">{error}</p>}
        <button type="submit" className="mt-5 w-full bg-[#0A192F] text-white px-6 py-3 text-sm tracking-wider uppercase" style={{ borderRadius: "2px" }}>Acceder</button>
      </form>
    </div>
  );
}

function FullCard({ children }) {
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-[#EAE3D5] p-10 text-center" style={{ borderRadius: "4px" }}>{children}</div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0A192F] text-white mt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 text-center">
        <p className="font-configurator italic text-[#E8C97A] text-lg">Tú diriges el negocio.</p>
        <p className="font-configurator text-2xl lg:text-3xl text-white mt-1">Nosotros organizamos el viaje.</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-white/70">
          <a href="mailto:hola@escapafy.com" className="inline-flex items-center gap-2 hover:text-[#E8C97A]"><Mail size={13} /> hola@escapafy.com</a>
          <a href="https://wa.me/34644088078" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-[#E8C97A]"><Phone size={13} /> WhatsApp</a>
        </div>
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────
// PRINT — Floating actions, header, summary
// ─────────────────────────────────────────────────────────────────
function PrintActions({ proposal }) {
  const [open, setOpen] = useState(false);

  const triggerPrint = (mode) => {
    setOpen(false);
    document.body.dataset.printMode = mode;
    // Give the browser one frame to apply attribute -> CSS
    requestAnimationFrame(() => {
      window.print();
      // Clean attribute right after the print dialog closes
      const cleanup = () => {
        delete document.body.dataset.printMode;
        window.removeEventListener("afterprint", cleanup);
      };
      window.addEventListener("afterprint", cleanup);
    });
  };

  return (
    <div className="fixed top-5 right-5 z-50 print:hidden" data-testid="print-actions" data-print-hide="true">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="print-toggle"
        className="bg-[#0A192F] hover:bg-[#16335A] text-[#FDFBF7] inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.15em] uppercase font-medium shadow-[0_8px_24px_rgba(10,25,47,0.2)] transition-colors"
        style={{ borderRadius: "2px" }}
        title="Imprimir o guardar como PDF"
      >
        <Printer size={14} /> Imprimir / PDF
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 bg-[#FDFBF7] border border-[#EAE3D5] shadow-[0_24px_48px_rgba(10,25,47,0.15)] min-w-[280px]"
          style={{ borderRadius: "2px" }}
          data-testid="print-menu"
        >
          <button
            type="button"
            onClick={() => triggerPrint("full")}
            data-testid="print-full"
            className="w-full text-left px-5 py-4 hover:bg-[#F3EFE7] transition-colors block border-b border-[#EAE3D5]"
          >
            <p className="font-configurator text-base text-[#0A192F] inline-flex items-center gap-2">
              <FileText size={14} /> Imprimir propuesta completa
            </p>
            <p className="text-[#3A506B] text-xs mt-1">Todas las opciones que te he preparado</p>
          </button>
          <button
            type="button"
            onClick={() => triggerPrint("selection")}
            data-testid="print-selection"
            className="w-full text-left px-5 py-4 hover:bg-[#F3EFE7] transition-colors block"
          >
            <p className="font-configurator text-base text-[#0A192F] inline-flex items-center gap-2">
              <Check size={14} /> Imprimir solo mi selección
            </p>
            <p className="text-[#3A506B] text-xs mt-1">Solo lo que has elegido + total final</p>
          </button>
        </div>
      )}
    </div>
  );
}

function PrintHeader({ proposal }) {
  const today = new Date().toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
  return (
    <header
      data-print-header="true"
      className="items-center justify-between border-b border-[#EAE3D5] pb-4 mb-8 px-6 pt-6"
      style={{ display: "none" }}
    >
      <img src={LOGO_URL} alt="Escapafy" style={{ height: "40px", width: "auto" }} />
      <div className="text-right">
        <p className="font-configurator text-base text-[#0A192F]">
          Propuesta para <strong>{proposal.client_name}</strong>
        </p>
        <p className="text-[#3A506B] text-xs mt-1">Generado el {today}</p>
        {proposal.destination && <p className="text-[#3A506B] text-xs">Destino: {proposal.destination}</p>}
      </div>
    </header>
  );
}

function PrintSummary({ proposal, summary, selections }) {
  const today = new Date().toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
  return (
    <section
      data-print-summary="true"
      className="mt-10 pt-8 border-t-2 border-[#0A192F]"
      style={{ display: "none" }}
    >
      <h2 className="font-configurator text-2xl text-[#0A192F] mb-5">Resumen económico</h2>
      {summary.lines.length === 0 ? (
        <p className="text-[#3A506B] italic">Sin selecciones registradas.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {summary.lines.map((l) => (
              <tr key={l.blockType}>
                <td style={{ padding: "8px 0", borderBottom: "1px solid #EAE3D5" }}>
                  <strong style={{ color: "#0A192F" }}>{l.label}</strong>
                  {l.name && <span style={{ color: "#3A506B", marginLeft: 8 }}>· {l.name}</span>}
                  {l.count != null && <span style={{ color: "#3A506B", marginLeft: 8 }}>· {l.count} {l.informational ? "incluidos" : "seleccionados"}</span>}
                </td>
                <td style={{ padding: "8px 0", borderBottom: "1px solid #EAE3D5", textAlign: "right" }}>
                  <strong style={{ color: "#0A192F" }}>{l.amount > 0 ? fmt(l.amount) : "—"}</strong>
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ padding: "16px 0 4px", fontFamily: "Cormorant Garamond, Playfair Display, serif", fontSize: "16pt", color: "#0A192F" }}>Total del viaje</td>
              <td style={{ padding: "16px 0 4px", textAlign: "right", fontFamily: "Cormorant Garamond, Playfair Display, serif", fontSize: "22pt", color: "#0A192F" }}>{fmt(summary.total)}</td>
            </tr>
            {summary.deposit > 0 && (
              <tr>
                <td style={{ padding: "4px 0", color: "#3A506B" }}>Señal para confirmar</td>
                <td style={{ padding: "4px 0", textAlign: "right", color: "#3A506B" }}>{fmt(summary.deposit)}</td>
              </tr>
            )}
            {summary.ivaPct > 0 && summary.pricesIncludeIva && (
              <tr>
                <td colSpan={2} style={{ paddingTop: 6, fontSize: "9pt", color: "#3A506B", fontStyle: "italic" }}>
                  IVA del {summary.ivaPct}% incluido en el total.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: "12mm", paddingTop: "6mm", borderTop: "1px solid #EAE3D5", fontSize: "9pt", color: "#3A506B" }}>
        <p>Propuesta válida sujeta a disponibilidad. Para confirmar tu viaje contacta con tu asesor de Escapafy:</p>
        <p style={{ marginTop: "3mm" }}>
          <strong>hola@escapafy.com</strong> · WhatsApp: +34 644 088 078 · escapafy.com
        </p>
        <p style={{ marginTop: "3mm", fontStyle: "italic" }}>Documento generado el {today}.</p>
      </div>
    </section>
  );
}
